/* oxlint-disable typescript/consistent-type-assertions */
import {
  ZENDESK_ARTICLES_IDS,
  ZENDESK_SECTIONS_IDS,
  type ZendeskCountryCode,
} from '@repo/shared-config/constants/zendesk';

const S3_BASE_URL = 'https://qonto-assets.s3.eu-central-1.amazonaws.com/faq_articles_zendesk';

/**
 * Fetches a per-slug mapping JSON from S3.
 *
 * Articles and sections share this file shape — a country to resource ID mapping — so only the slug
 * distinguishes them. Throws on any non-2xx response so the caller can retry and ultimately report
 * to Sentry: a non-2xx means the file has not been published for this slug yet, which is actionable.
 *
 * @param slug - The article or section slug
 * @returns Country to resource ID mapping
 */
export async function fetchJsonFromS3(
  slug: string
): Promise<Record<ZendeskCountryCode, string | null>> {
  const response = await fetch(`${S3_BASE_URL}/${slug}.json`);

  if (!response.ok) {
    throw new Error(`S3 fetch failed with status ${response.status}`);
  }

  return response.json() as Promise<Record<ZendeskCountryCode, string | null>>;
}

/**
 * Extracts the article URL from a country mapping.
 *
 * @param articleMapping - The S3 JSON data (country - article ID mapping)
 * @param organizationCountry - The Zendesk country code
 * @param helpCenterBaseUrl - The base help center URL
 * @returns string - The full article URL or help center URL as fallback
 */
export function getArticleUrlFromMapping(
  articleMapping: Record<ZendeskCountryCode, string | null>,
  organizationCountry: string,
  helpCenterBaseUrl: string
): string {
  if (articleMapping[organizationCountry as ZendeskCountryCode]) {
    return `${helpCenterBaseUrl}/articles/${articleMapping[organizationCountry as ZendeskCountryCode]}`;
  }

  return helpCenterBaseUrl;
}

/**
 * Gets the article ID from config.
 *
 * @param articleSlug - The article slug
 * @param organizationCountry - The Zendesk country code
 * @returns string | null - The article ID from config, or null if not found
 */
export function getArticleIdFromConfig(
  articleSlug: string,
  organizationCountry: string
): string | null {
  return ZENDESK_ARTICLES_IDS[articleSlug]?.[organizationCountry as ZendeskCountryCode]
    ? String(ZENDESK_ARTICLES_IDS[articleSlug][organizationCountry as ZendeskCountryCode])
    : null;
}

export interface GetZendeskArticleUrlParams {
  articleSlug: string;
  helpCenterUrl: string;
  organizationCountry: string;
  s3Data?: Record<ZendeskCountryCode, string | null> | null;
}

/**
 * Gets Zendesk article URL
 *
 * @param articleSlug - The Zendesk article slug
 * @param helpCenterUrl - The base help center URL (e.g., 'https://help.qonto.com/fr')
 * @param organizationCountry - The Zendesk country code
 * @param s3Data - The S3 JSON data (country - article ID mapping), or null if not available
 * @returns The full article URL
 */
export function getZendeskArticleUrl({
  articleSlug,
  helpCenterUrl,
  organizationCountry,
  s3Data,
}: GetZendeskArticleUrlParams): string {
  let url = helpCenterUrl;

  if (s3Data) {
    url = getArticleUrlFromMapping(s3Data, organizationCountry, helpCenterUrl);
  } else {
    const articleId = getArticleIdFromConfig(articleSlug, organizationCountry);

    if (articleId) {
      url = `${helpCenterUrl}/articles/${articleId}`;
    }
  }

  return url;
}

/**
 * Extracts the section URL from a country mapping.
 *
 * Sections live under `/sections/`, not `/articles/` — a section ID on the articles path resolves to
 * nothing.
 *
 * @param sectionMapping - The S3 JSON data (country - section ID mapping)
 * @param organizationCountry - The Zendesk country code
 * @param helpCenterBaseUrl - The base help center URL
 * @returns string - The full section URL or help center URL as fallback
 */
export function getSectionUrlFromMapping(
  sectionMapping: Record<ZendeskCountryCode, string | null>,
  organizationCountry: string,
  helpCenterBaseUrl: string
): string {
  if (sectionMapping[organizationCountry as ZendeskCountryCode]) {
    return `${helpCenterBaseUrl}/sections/${sectionMapping[organizationCountry as ZendeskCountryCode]}`;
  }

  return helpCenterBaseUrl;
}

/**
 * Gets the section ID from config.
 *
 * @param sectionSlug - The section slug
 * @param organizationCountry - The Zendesk country code
 * @returns string | null - The section ID from config, or null if not found
 */
export function getSectionIdFromConfig(
  sectionSlug: string,
  organizationCountry: string
): string | null {
  return ZENDESK_SECTIONS_IDS[sectionSlug]?.[organizationCountry as ZendeskCountryCode]
    ? String(ZENDESK_SECTIONS_IDS[sectionSlug][organizationCountry as ZendeskCountryCode])
    : null;
}

export interface GetZendeskSectionUrlParams {
  sectionSlug: string;
  helpCenterUrl: string;
  organizationCountry: string;
  s3Data?: Record<ZendeskCountryCode, string | null> | null;
}

/**
 * Gets Zendesk section URL
 *
 * @param sectionSlug - The Zendesk section slug
 * @param helpCenterUrl - The base help center URL (e.g., 'https://help.qonto.com/fr')
 * @param organizationCountry - The Zendesk country code
 * @param s3Data - The S3 JSON data (country - section ID mapping), or null if not available
 * @returns The full section URL
 */
export function getZendeskSectionUrl({
  sectionSlug,
  helpCenterUrl,
  organizationCountry,
  s3Data,
}: GetZendeskSectionUrlParams): string {
  let url = helpCenterUrl;

  if (s3Data) {
    url = getSectionUrlFromMapping(s3Data, organizationCountry, helpCenterUrl);
  } else {
    const sectionId = getSectionIdFromConfig(sectionSlug, organizationCountry);

    if (sectionId) {
      url = `${helpCenterUrl}/sections/${sectionId}`;
    }
  }

  return url;
}
